import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Papa from "papaparse";
import Icon from "@mdi/react";
import {
  mdiClose,
  mdiCloudUploadOutline,
  mdiFileDocumentOutline,
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
} from "@mdi/js";
import { fetchPost } from "../../../../service/api_client";
import {
  useToast,
  ToastType,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

interface CsvRow {
  outward_id: string;
  tracking_id: string;
  pod_filename: string;
  delivery_remarks: string;
}

interface RowIssue {
  rowNum: number;
  reason: string;
}

interface ApiResultRow {
  row: number;
  outward_id: string;
  reason?: string;
}

const MAX_ROWS = 50;
const REQUIRED_HEADERS = ["outward_id", "tracking_id"];
const OPTIONAL_HEADERS = ["pod_filename", "delivery_remarks"];

const baseName = (name: string) =>
  name
    .replace(/\.[^./\\]+$/, "")
    .trim()
    .toLowerCase();

const BulkPodUploadModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [podFiles, setPodFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [headerIssues, setHeaderIssues] = useState<string[]>([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{
    total: number;
    success_count: number;
    error_count: number;
    successes: ApiResultRow[];
    errors: ApiResultRow[];
  } | null>(null);

  const { podByName, podCollisions } = useMemo(() => {
    const m = new Map<string, File>();
    const collisions = new Set<string>();
    for (const f of podFiles) {
      const key = baseName(f.name);
      if (m.has(key)) collisions.add(key);
      m.set(key, f);
    }
    return { podByName: m, podCollisions: collisions };
  }, [podFiles]);

  const rowIssues: RowIssue[] = useMemo(() => {
    const issues: RowIssue[] = [];
    const seen = new Set<string>();
    rows.forEach((r, i) => {
      const rowNum = i + 2;
      const oid = (r.outward_id ?? "").trim();
      const tid = (r.tracking_id ?? "").trim();
      const pod = (r.pod_filename ?? "").trim();

      if (!oid) issues.push({ rowNum, reason: "outward_id is empty" });
      if (!tid) issues.push({ rowNum, reason: "tracking_id is empty" });
      if (oid) {
        if (seen.has(oid))
          issues.push({ rowNum, reason: `duplicate outward_id '${oid}'` });
        seen.add(oid);
      }
      if (pod) {
        const key = baseName(pod);
        if (!podByName.has(key)) {
          issues.push({
            rowNum,
            reason: `POD file '${pod}' not attached`,
          });
        } else if (podCollisions.has(key)) {
          issues.push({
            rowNum,
            reason: `multiple POD files share the name '${key}' — rename so each is unique`,
          });
        }
      }
    });
    return issues;
  }, [rows, podByName, podCollisions]);

  if (!open) return null;

  const isOverLimit = rows.length > MAX_ROWS;
  const hasBlockers =
    headerIssues.length > 0 || rowIssues.length > 0 || isOverLimit;
  const canSubmit = csvFile && rows.length > 0 && !hasBlockers && !submitting;

  const handleCsv = (file: File | null) => {
    setResult(null);
    setRows([]);
    setHeaderIssues([]);
    if (!file) {
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
    setParsing(true);
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.replace(/^﻿/, "").trim().toLowerCase(),
      complete: (out) => {
        setParsing(false);
        const fields = out.meta.fields ?? [];
        const missing = REQUIRED_HEADERS.filter((h) => !fields.includes(h));
        const unknown = fields.filter(
          (h) => ![...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].includes(h),
        );
        const hdrIssues: string[] = [];
        if (missing.length)
          hdrIssues.push(`Missing required columns: ${missing.join(", ")}`);
        if (unknown.length)
          hdrIssues.push(`Unknown columns (ignored): ${unknown.join(", ")}`);
        setHeaderIssues(hdrIssues);
        setRows(out.data as CsvRow[]);
      },
      error: (err) => {
        setParsing(false);
        showToast({
          type: ToastType.error,
          text: `CSV parse error: ${err.message}`,
        });
      },
    });
  };

  const handlePods = (files: FileList | null) => {
    if (!files) return;
    setResult(null);
    setPodFiles(Array.from(files));
  };

  const downloadTemplate = () => {
    const csv =
      "outward_id,tracking_id,pod_filename,delivery_remarks\n" +
      "ORD-EXAMPLE,TRK123,POD-001,Delivered to security\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bulk_pod_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const downloadErrors = () => {
    if (!result?.errors.length) return;
    const rowsCsv = [
      "row,outward_id,reason",
      ...result.errors.map(
        (e) =>
          `${e.row},${JSON.stringify(e.outward_id)},${JSON.stringify(e.reason ?? "")}`,
      ),
    ].join("\n");
    const blob = new Blob([rowsCsv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bulk_pod_errors.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const submit = async () => {
    if (!csvFile) return;
    setSubmitting(true);
    setConfirming(false);
    setUploadPercent(0);
    try {
      const fd = new FormData();
      fd.append("csv", csvFile, csvFile.name);
      for (const f of podFiles) fd.append("pods", f, f.name);

      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/bulk-mark-delivered`;
      const res = await fetchPost(url, fd, undefined, {
        onUploadProgress: (e: any) => {
          if (e?.total) {
            setUploadPercent(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      if (!res?.success) {
        throw new Error(res?.error || "Bulk delivery failed");
      }
      setResult({
        total: res.total,
        success_count: res.success_count,
        error_count: res.error_count,
        successes: res.successes ?? [],
        errors: res.errors ?? [],
      });
      showToast({
        type: res.error_count > 0 ? ToastType.warning : ToastType.success,
        text: `Bulk delivery: ${res.success_count}/${res.total} succeeded`,
      });
      if (res.success_count > 0 && onSuccess) onSuccess();
      // Clean success — no per-row errors to review, just close.
      // Partial / total failure — keep the modal open so the user can see
      // the error rows and download the errors CSV.
      if (res.error_count === 0) {
        onClose();
      }
    } catch (err: any) {
      showToast({ type: ToastType.error, text: err.message });
    } finally {
      setSubmitting(false);
      setUploadPercent(0);
    }
  };

  return createPortal(
    <div
      id="warehouse-outward-bulk-pod-upload-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-5xl my-auto flex flex-col rounded-xl bg-white shadow-xl overflow-hidden wh-modal-xs"
        style={{ maxHeight: "90vh" }}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-semibold">
              Bulk Upload POD &amp; Mark Delivered
            </h2>
            <p className="text-xs text-gray-500">
              Max {MAX_ROWS} orders per upload. POD is optional per row.
            </p>
          </div>
          <button
            id="warehouse-outward-bulk-pod-close-icon-btn"
            onClick={onClose}
            disabled={submitting}
            className={`rounded-full p-1 ${
              submitting ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"
            }`}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* Step 1: CSV */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-semibold">CSV file</label>
              <button
                id="warehouse-outward-bulk-pod-download-template-btn"
                type="button"
                onClick={downloadTemplate}
                className="text-xs text-blue-600 hover:underline"
              >
                Download template
              </button>
            </div>
            <input
              id="warehouse-outward-bulk-pod-csv-input"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => handleCsv(e.target.files?.[0] ?? null)}
              className="block w-full text-sm border rounded-md px-3 py-2"
            />
            {csvFile && (
              <p className="mt-1 text-xs text-gray-600 flex items-center gap-1">
                <Icon path={mdiFileDocumentOutline} size={0.6} />
                {csvFile.name} — {rows.length} row(s)
              </p>
            )}
            {headerIssues.map((h) => (
              <p
                key={h}
                className="mt-1 text-xs text-red-600 flex items-center gap-1"
              >
                <Icon path={mdiAlertCircleOutline} size={0.6} />
                {h}
              </p>
            ))}
          </div>

          {/* Step 2: PODs */}
          <div>
            <label className="text-sm font-semibold block mb-1">
              POD files (optional, multi-select)
            </label>
            <input
              id="warehouse-outward-bulk-pod-files-input"
              type="file"
              multiple
              accept="application/pdf,image/*"
              onChange={(e) => handlePods(e.target.files)}
              className="block w-full text-sm border rounded-md px-3 py-2"
            />
            <p className="mt-1 text-xs text-gray-600 flex items-center gap-1">
              <Icon path={mdiCloudUploadOutline} size={0.6} />
              {podFiles.length} POD file(s) attached
            </p>
          </div>

          {/* Preview */}
          {rows.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold">
                  Preview ({rows.length} row{rows.length !== 1 ? "s" : ""})
                </h3>
                {isOverLimit && (
                  <span className="text-xs text-red-600">
                    Over the {MAX_ROWS}-row limit — trim the CSV.
                  </span>
                )}
              </div>
              <div
                className="overflow-auto border rounded-md"
                style={{ maxHeight: "50vh" }}
              >
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">outward_id</th>
                      <th className="p-2 text-left">tracking_id</th>
                      <th className="p-2 text-left">pod_filename</th>
                      <th className="p-2 text-left">delivery_remarks</th>
                      <th className="p-2 text-left">POD match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const pod = (r.pod_filename ?? "").trim();
                      const matched = !pod || podByName.has(baseName(pod));
                      return (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="p-2">{i + 2}</td>
                          <td className="p-2 font-mono">{r.outward_id}</td>
                          <td className="p-2 font-mono">{r.tracking_id}</td>
                          <td className="p-2 font-mono">{pod || "—"}</td>
                          <td className="p-2">{r.delivery_remarks}</td>
                          <td className="p-2">
                            {!pod ? (
                              <span className="text-gray-400">no POD</span>
                            ) : matched ? (
                              <span className="text-green-600 inline-flex items-center gap-1">
                                <Icon path={mdiCheckCircleOutline} size={0.6} />
                                matched
                              </span>
                            ) : (
                              <span className="text-red-600 inline-flex items-center gap-1">
                                <Icon path={mdiAlertCircleOutline} size={0.6} />
                                missing
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rowIssues.length > 0 && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-semibold text-red-700">
                    {rowIssues.length} blocker(s) — fix before submitting
                  </p>
                  <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 text-sm text-red-600 list-disc list-inside">
                    {rowIssues.map((iss, idx) => (
                      <li key={idx}>
                        Row {iss.rowNum}: {iss.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="rounded-md border bg-gray-50 p-4">
              <h3 className="text-sm font-semibold mb-2">
                Result: {result.success_count} / {result.total} delivered
                {result.error_count > 0 && (
                  <span className="text-red-600">
                    {" "}
                    — {result.error_count} failed
                  </span>
                )}
              </h3>
              {result.errors.length > 0 && (
                <>
                  <ul className="max-h-40 overflow-y-auto text-xs space-y-0.5 list-disc list-inside text-red-700">
                    {result.errors.map((e, idx) => (
                      <li key={idx}>
                        Row {e.row} ({e.outward_id}): {e.reason}
                      </li>
                    ))}
                  </ul>
                  <button
                    id="warehouse-outward-bulk-pod-download-errors-btn"
                    onClick={downloadErrors}
                    className="mt-2 text-xs text-blue-600 hover:underline"
                  >
                    Download errors as CSV
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
          <button
            id="warehouse-outward-bulk-pod-footer-close-btn"
            onClick={onClose}
            disabled={submitting}
            className={`h-10 px-4 rounded-md border border-gray-300 text-sm ${
              submitting
                ? "opacity-50 cursor-not-allowed"
                : "text-gray-700 hover:bg-gray-50"
            }`}
          >
            Close
          </button>
          {!confirming ? (
            <button
              id="warehouse-outward-bulk-pod-mark-delivered-btn"
              onClick={() => setConfirming(true)}
              disabled={!canSubmit}
              className={`h-10 px-5 rounded-md text-sm font-medium ${
                canSubmit
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Mark {rows.length} as Delivered
            </button>
          ) : (
            <>
              <span className="self-center text-sm text-gray-700">
                Confirm marking {rows.length} order(s) as DELIVERED?
              </span>
              <button
                id="warehouse-outward-bulk-pod-confirm-no-btn"
                onClick={() => setConfirming(false)}
                className="h-10 px-3 rounded-md border border-gray-300 text-sm"
              >
                No
              </button>
              <button
                id="warehouse-outward-bulk-pod-confirm-yes-btn"
                onClick={submit}
                className="h-10 px-5 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
              >
                Yes, deliver
              </button>
            </>
          )}
        </div>

        {parsing && <LoadingWidget />}

        {submitting && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/95">
            <div className="text-center max-w-md px-6">
              {uploadPercent < 100 ? (
                <>
                  <p className="text-sm font-semibold text-gray-800 mb-3">
                    Uploading CSV + {podFiles.length} POD
                    {podFiles.length !== 1 ? "s" : ""}…
                  </p>
                  <div className="w-80 h-4 bg-gray-200 border border-gray-300 rounded-full overflow-hidden mx-auto shadow-inner">
                    <div
                      className="h-full bg-blue-600 rounded-full transition-all duration-300"
                      style={{ width: `${Math.max(uploadPercent, 3)}%` }}
                    />
                  </div>
                  <p className="text-base font-semibold text-blue-700 mt-2">
                    {uploadPercent}%
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                  <p className="text-sm font-semibold text-gray-800">
                    Processing {rows.length} order
                    {rows.length !== 1 ? "s" : ""} on server…
                  </p>
                </>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Please don’t close this tab.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default BulkPodUploadModal;
